import React, { useState, useMemo } from 'react';
import {
  Search,
  Filter,
  Upload,
  MoreVertical,
  Eye,
  Edit2,
  Clock,
  MapPin,
  Smartphone,
  Monitor,
  Fingerprint,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Coffee,
  ChevronDown,
  ArrowUpDown,
  UserPlus,
  Printer,
  RefreshCw,
  X,
} from 'lucide-react';
import { ATTENDANCE_STATUS, DEPARTMENTS, CHECK_IN_METHODS, formatTime } from './attendanceData';
import toast from 'react-hot-toast';

// Filter Dropdown Component
const FilterDropdown = ({ label, value, options, onChange, icon: Icon }) => {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white cursor-pointer"
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = ATTENDANCE_STATUS[status] || ATTENDANCE_STATUS.present;
  const icons = {
    present: CheckCircle2,
    absent: XCircle,
    late: AlertCircle,
    leave: Coffee,
    halfday: Clock,
  };
  const Icon = icons[status] || CheckCircle2;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${config.bg} ${config.text} ${config.border}`}>
      <Icon size={12} />
      {config.label}
    </span>
  );
};

// Method Icon Component
const MethodIcon = ({ method }) => {
  const icons = {
    biometric: { icon: Fingerprint, label: 'Biometric' },
    mobile: { icon: Smartphone, label: 'Mobile App' },
    web: { icon: Monitor, label: 'Web Portal' },
    manual: { icon: Edit2, label: 'Manual Entry' },
  };
  const config = icons[method] || icons.web;
  const Icon = config.icon;

  return (
    <span className="inline-flex items-center gap-1 text-xs text-gray-500" title={config.label}>
      <Icon size={12} />
    </span>
  );
};

// Employee Details Modal
const EmployeeDetailsModal = ({ employee, onClose, onEdit }) => {
  if (!employee) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Attendance Details</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition">
            <X size={18} />
          </button>
        </div>

        <div className="p-5">
          {/* Employee Info */}
          <div className="flex items-center gap-4 mb-5">
            <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-lg">
              {employee.name.split(' ').map(n => n[0]).join('')}
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">{employee.name}</h4>
              <p className="text-sm text-gray-500">{employee.empCode} • {employee.designation}</p>
              <p className="text-xs text-gray-400">{employee.department}</p>
            </div>
          </div>

          {/* Attendance Info Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Check In</p>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-gray-900">{formatTime(employee.checkIn) || '--:--'}</p>
                {employee.method && <MethodIcon method={employee.method} />}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Check Out</p>
              <p className="font-semibold text-gray-900">{formatTime(employee.checkOut) || '--:--'}</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Hours Worked</p>
              <p className="font-semibold text-gray-900">{employee.hoursWorked || '--'}</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Status</p>
              <StatusBadge status={employee.status} />
            </div>

            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Shift</p>
              <p className="font-semibold text-gray-900">{employee.shiftTiming || employee.shift}</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Location</p>
              <div className="flex items-center gap-1">
                <MapPin size={12} className="text-gray-400" />
                <p className="font-medium text-gray-900 text-sm">{employee.location || 'N/A'}</p>
              </div>
            </div>
          </div>

          {employee.overtimeHours && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <span className="font-medium">Overtime:</span> {employee.overtimeHours}
              </p>
            </div>
          )}

          {employee.remarks && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <span className="font-medium">Remarks:</span> {employee.remarks}
              </p>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition text-sm font-medium"
          >
            Close
          </button>
          <button
            onClick={() => onEdit(employee)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium flex items-center gap-2"
          >
            <Edit2 size={14} />
            Edit Attendance
          </button>
        </div>
      </div>
    </div>
  );
};

// Edit Attendance Modal
const EditAttendanceModal = ({ employee, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    checkIn: employee?.checkIn || '',
    checkOut: employee?.checkOut || '',
    status: employee?.status || 'present',
    remarks: employee?.remarks || '',
  });

  const handleSave = () => {
    onSave({ ...employee, ...formData });
    toast.success(`Attendance updated for ${employee.name}`);
    onClose();
  };

  if (!employee) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Edit Attendance</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-sm">
              {employee.name.split(' ').map(n => n[0]).join('')}
            </div>
            <div>
              <p className="font-medium text-gray-900">{employee.name}</p>
              <p className="text-xs text-gray-500">{employee.empCode}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Check In</label>
              <input
                type="time"
                value={formData.checkIn}
                onChange={(e) => setFormData({ ...formData, checkIn: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Check Out</label>
              <input
                type="time"
                value={formData.checkOut}
                onChange={(e) => setFormData({ ...formData, checkOut: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {Object.entries(ATTENDANCE_STATUS).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
            <textarea
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Add any notes..."
            />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

// Mark Attendance Modal
const MarkAttendanceModal = ({ employees, onClose, onMark }) => {
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [formData, setFormData] = useState({
    checkIn: '',
    checkOut: '',
    status: 'present',
    method: 'manual',
    remarks: '',
  });

  const handleMark = () => {
    const emp = employees.find(e => e.id === parseInt(selectedEmployee));
    if (emp) {
      onMark({ ...emp, ...formData });
      toast.success(`Attendance marked for ${emp.name}`);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Mark Attendance</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Employee</label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Choose employee...</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.name} ({emp.empCode})</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Check In</label>
              <input
                type="time"
                value={formData.checkIn}
                onChange={(e) => setFormData({ ...formData, checkIn: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Check Out</label>
              <input
                type="time"
                value={formData.checkOut}
                onChange={(e) => setFormData({ ...formData, checkOut: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {Object.entries(ATTENDANCE_STATUS).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
            <textarea
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Add any notes..."
            />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleMark}
            disabled={!selectedEmployee}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Mark Attendance
          </button>
        </div>
      </div>
    </div>
  );
};

// Action Menu Component
const ActionMenu = ({ employee, onView, onEdit, onRegularize }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition"
      >
        <MoreVertical size={16} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-40 z-20">
            <button
              onClick={() => { onView(employee); setIsOpen(false); }}
              className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <Eye size={14} /> View Details
            </button>
            <button
              onClick={() => { onEdit(employee); setIsOpen(false); }}
              className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <Edit2 size={14} /> Edit Attendance
            </button>
            <button
              onClick={() => { onRegularize(employee); setIsOpen(false); }}
              className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <Clock size={14} /> Regularize
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// Main Component
export default function DailyAttendanceLog({
  attendanceLog,
  employees,
  onUpdateAttendance,
  onRegularize,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterShift, setFilterShift] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  // Modal states
  const [viewEmployee, setViewEmployee] = useState(null);
  const [editEmployee, setEditEmployee] = useState(null);
  const [showMarkModal, setShowMarkModal] = useState(false);

  // Filter and sort data
  const filteredData = useMemo(() => {
    let data = [...attendanceLog];

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      data = data.filter(emp =>
        emp.name.toLowerCase().includes(query) ||
        emp.empCode.toLowerCase().includes(query) ||
        emp.department.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      data = data.filter(emp => emp.status === filterStatus);
    }

    // Apply department filter
    if (filterDepartment !== 'all') {
      data = data.filter(emp => emp.department === filterDepartment);
    }

    // Apply shift filter
    if (filterShift !== 'all') {
      data = data.filter(emp => emp.shift === filterShift);
    }

    // Apply sorting
    data.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === 'checkIn' || sortConfig.key === 'checkOut') {
        aVal = aVal || '99:99';
        bVal = bVal || '99:99';
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return data;
  }, [attendanceLog, searchQuery, filterStatus, filterDepartment, filterShift, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handlePrint = () => {
    window.print();
    toast.success('Print dialog opened');
  };

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    ...Object.entries(ATTENDANCE_STATUS).map(([key, val]) => ({ value: key, label: val.label })),
  ];

  const departmentOptions = [
    { value: 'all', label: 'All Departments' },
    ...DEPARTMENTS.map(d => ({ value: d.name, label: d.name })),
  ];

  const shiftOptions = [
    { value: 'all', label: 'All Shifts' },
    { value: 'MS', label: 'Morning (MS)' },
    { value: 'DS', label: 'Day (DS)' },
    { value: 'ES', label: 'Evening (ES)' },
    { value: 'NS', label: 'Night (NS)' },
  ];

  return (
    <div className="space-y-4">
      {/* Filters & Actions Bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Search */}
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-64 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Filters */}
            <FilterDropdown
              icon={Filter}
              value={filterStatus}
              options={statusOptions}
              onChange={setFilterStatus}
            />

            <FilterDropdown
              icon={Filter}
              value={filterDepartment}
              options={departmentOptions}
              onChange={setFilterDepartment}
            />

            <FilterDropdown
              icon={Clock}
              value={filterShift}
              options={shiftOptions}
              onChange={setFilterShift}
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">
              {filteredData.length} of {attendanceLog.length} records
            </span>
            <button
              onClick={() => setShowMarkModal(true)}
              className="flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 transition font-medium"
            >
              <UserPlus size={14} />
              Mark Attendance
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition"
            >
              <Printer size={14} />
              Print
            </button>
          </div>
        </div>
      </div>

      {/* Attendance Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('name')}
                    className="flex items-center gap-1 text-xs font-semibold text-gray-600 uppercase tracking-wider hover:text-blue-600"
                  >
                    Employee
                    <ArrowUpDown size={12} />
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Shift
                </th>
                <th className="px-4 py-3 text-center">
                  <button
                    onClick={() => handleSort('checkIn')}
                    className="flex items-center gap-1 text-xs font-semibold text-gray-600 uppercase tracking-wider hover:text-blue-600 mx-auto"
                  >
                    Check In
                    <ArrowUpDown size={12} />
                  </button>
                </th>
                <th className="px-4 py-3 text-center">
                  <button
                    onClick={() => handleSort('checkOut')}
                    className="flex items-center gap-1 text-xs font-semibold text-gray-600 uppercase tracking-wider hover:text-blue-600 mx-auto"
                  >
                    Check Out
                    <ArrowUpDown size={12} />
                  </button>
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Hours
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredData.map((emp) => (
                <tr key={emp.id} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-xs">
                        {emp.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{emp.name}</p>
                        <p className="text-xs text-gray-500">{emp.empCode}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{emp.department}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded">
                      {emp.shift}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {emp.checkIn ? (
                      <div className="flex items-center justify-center gap-1">
                        <span className="font-medium text-gray-900">{formatTime(emp.checkIn)}</span>
                        {emp.method && <MethodIcon method={emp.method} />}
                      </div>
                    ) : (
                      <span className="text-gray-400">--:--</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {emp.checkOut ? (
                      <span className="font-medium text-gray-900">{formatTime(emp.checkOut)}</span>
                    ) : (
                      <span className="text-gray-400">--:--</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`font-medium ${emp.hoursWorked ? 'text-gray-900' : 'text-gray-400'}`}>
                      {emp.hoursWorked || '--'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge status={emp.status} />
                  </td>
                  <td className="px-4 py-3">
                    {emp.location && (
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <MapPin size={12} />
                        {emp.location}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <ActionMenu
                      employee={emp}
                      onView={setViewEmployee}
                      onEdit={setEditEmployee}
                      onRegularize={onRegularize}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredData.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-gray-500">No attendance records found</p>
            <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
          </div>
        )}
      </div>

      {/* Modals */}
      {viewEmployee && (
        <EmployeeDetailsModal
          employee={viewEmployee}
          onClose={() => setViewEmployee(null)}
          onEdit={(emp) => {
            setViewEmployee(null);
            setEditEmployee(emp);
          }}
        />
      )}

      {editEmployee && (
        <EditAttendanceModal
          employee={editEmployee}
          onClose={() => setEditEmployee(null)}
          onSave={onUpdateAttendance}
        />
      )}

      {showMarkModal && (
        <MarkAttendanceModal
          employees={employees}
          onClose={() => setShowMarkModal(false)}
          onMark={onUpdateAttendance}
        />
      )}
    </div>
  );
}
